import { apiClient } from '../apiClient';

export interface CurriculumTemplate {
  id: string;
  title: string;
  slug: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateListItem {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  module_count: number;
  topic_count: number;
  total_hours: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface CurriculumModule {
  id: string;
  template_id: string;
  title: string;
  description?: string;
  sort_order: number;
  phase: string;
  estimated_days?: number;
  topics: CurriculumTopic[];
}

export interface CurriculumTopic {
  id: string;
  module_id: string;
  title: string;
  description?: string;
  sort_order: number;
  estimated_hours?: number;
  topic_type: 'lecture' | 'lab' | 'project';
}

export interface TemplateDetail extends CurriculumTemplate {
  modules: CurriculumModule[];
}

export interface BatchCurriculum {
  id: string;
  batch_id: string;
  source_template_id: string;
  created_at: string;
}

export interface TemplateBatchItem {
  id: string;
  name: string;
  org_name: string;
}

export const curriculumApi = {
  listTemplates: () =>
    apiClient.get<TemplateListItem[]>('/api/v1/admin/curriculum/templates'),

  getTemplate: (id: string) =>
    apiClient.get<TemplateDetail>(`/api/v1/admin/curriculum/templates/${id}`),

  createTemplate: (data: { title: string; slug: string; description?: string }) =>
    apiClient.post<CurriculumTemplate>('/api/v1/admin/curriculum/templates', data),

  updateTemplate: (id: string, data: Record<string, unknown>) =>
    apiClient.put<CurriculumTemplate>(`/api/v1/admin/curriculum/templates/${id}`, data),

  deleteTemplate: (id: string) =>
    apiClient.delete(`/api/v1/admin/curriculum/templates/${id}`),

  listTemplateBatches: (id: string) =>
    apiClient.get<TemplateBatchItem[]>(`/api/v1/admin/curriculum/templates/${id}/batches`),

  forkTemplate: (id: string, batchId: string) =>
    apiClient.post<BatchCurriculum>(`/api/v1/admin/curriculum/templates/${id}/fork`, { batch_id: batchId }),

  createModule: (data: {
    template_id: string; title: string; description?: string;
    sort_order: number; phase: string; estimated_days?: number;
  }) => apiClient.post<CurriculumModule>('/api/v1/admin/curriculum/modules', data),

  updateModule: (id: string, data: Record<string, unknown>) =>
    apiClient.put<CurriculumModule>(`/api/v1/admin/curriculum/modules/${id}`, data),

  deleteModule: (id: string) =>
    apiClient.delete(`/api/v1/admin/curriculum/modules/${id}`),

  createTopic: (data: {
    module_id: string; title: string; description?: string;
    sort_order: number; estimated_hours?: number; topic_type?: string;
  }) => apiClient.post<CurriculumTopic>('/api/v1/admin/curriculum/topics', data),

  updateTopic: (id: string, data: Record<string, unknown>) =>
    apiClient.put<CurriculumTopic>(`/api/v1/admin/curriculum/topics/${id}`, data),

  deleteTopic: (id: string) =>
    apiClient.delete(`/api/v1/admin/curriculum/topics/${id}`),
};
